import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { FormattedMessage, injectIntl } from 'react-intl';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import * as mixins from 'view/styles/mixins';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Card } from '@atlaskit/media-card';
import type { FileIdentifier } from '@atlaskit/media-client';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { Button } from '@atlassian/help-center-common-component/analytics/button';
import type { AttachmentFile } from '@atlassian/help-center-common-component/fields/types';
import { InteractiveElement } from '@atlassian/help-center-common-component/interactive-element';
import i18n from '../messages';
import type { AttachmentFileFailure, AttachmentSource, AttachmentFileInProgress } from '../types';
import { UploadStatus } from '../upload-status';

interface AttachmentDumbsProps {
    disabled?: boolean;
    hasError?: boolean;
    uploadedAttachments: AttachmentFile[];
    uploadsInProgress: AttachmentFileInProgress[];
    failedUploads: AttachmentFileFailure[];
    // Used to initialize Media Card
    mediaClientConfig: MediaClientConfig;
    collection: string;

    onBrowseClicked: () => void | null;

    onRemoveAttached: (fileId: string) => void;
    onRemoveInProgress: (fileId: string, source?: AttachmentSource) => void;
    onRemoveError: (fileId: string) => void;
}

export class AttachmentsDumb extends React.PureComponent<AttachmentDumbsProps & InjectedIntlProps> {
    onRemoveAttachedListener = (file: AttachmentFile) => {
        return () => {
            this.props.onRemoveAttached(file.id);
        };
    };
    onRemoveInProgressListener = (file: AttachmentFileInProgress) => {
        return () => {
            this.props.onRemoveInProgress(file.id, file.source);
        };
    };
    onRemoveErrorListener = (file: AttachmentFileFailure) => {
        return () => {
            this.props.onRemoveError(file.id);
        };
    };

    attachedFiles() {
        const { collection, mediaClientConfig, intl, uploadedAttachments } = this.props;
        if (uploadedAttachments && uploadedAttachments.length) {
            return (
                <AttachedFilesContainer>
                    {uploadedAttachments.map((file) => {
                        const identifier: FileIdentifier = {
                            mediaItemType: 'file',
                            id: file.id || '',
                            collectionName: collection,
                        };
                        const removeLabel = intl.formatMessage(i18n.attachmentRemoveLabel);
                        const actions = [
                            {
                                handler: this.onRemoveAttachedListener(file),
                                label: removeLabel,
                                icon: <CrossIconWrapper removeLabel={removeLabel} />,
                            },
                        ];
                        return (
                            <AttachedFile key={file.id}>
                                <Card
                                    mediaClientConfig={mediaClientConfig}
                                    appearance="image"
                                    identifier={identifier}
                                    actions={actions}
                                    // This is so we can fit 3 cards in one row in the request create paper.
                                    dimensions={{ width: '145px', height: '145px' }}
                                />
                            </AttachedFile>
                        );
                    })}
                </AttachedFilesContainer>
            );
        }
        return null;
    }

    onBrowseClicked = () => {
        this.props.onBrowseClicked && this.props.onBrowseClicked();
    };

    render() {
        const { uploadsInProgress, failedUploads, hasError, disabled, intl } = this.props;
        return (
            <div>
                <DropzoneContainer hasError={hasError}>
                    <DropzoneContent>
                        <DropzoneContentText>
                            <FormattedMessage {...i18n.attachmentField} />
                        </DropzoneContentText>
                        <Button
                            isDisabled={disabled}
                            onClick={this.onBrowseClicked}
                            actionSubjectId="browseAttachments"
                            aria-label={intl.formatMessage(i18n.attachmentFieldButtonAriaLabel)}
                        >
                            <FormattedMessage {...i18n.attachmentFieldButton} />
                        </Button>
                        {this.attachedFiles()}
                    </DropzoneContent>
                </DropzoneContainer>

                {uploadsInProgress.map((uploading) => (
                    <UploadStatus
                        key={uploading.id}
                        {...uploading}
                        onRemove={this.onRemoveInProgressListener(uploading)}
                    />
                ))}

                {failedUploads.map((error) => (
                    <UploadStatus key={error.id} {...error} failed onRemove={this.onRemoveErrorListener(error)} />
                ))}
            </div>
        );
    }
}

interface CrossIconWrapperProps {
    removeLabel: string;
}

const CrossIconWrapper: React.FC<CrossIconWrapperProps> = ({ removeLabel }) => {
    return (
        <InteractiveElement aria-label={removeLabel}>
            <CrossIcon label="" size="small" />
        </InteractiveElement>
    );
};

export default injectIntl(AttachmentsDumb);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const DropzoneContent = styled.div`
    padding: ${token('space.200', '16px')};
    text-align: center;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const DropzoneContentText = styled.div`
    margin-bottom: ${token('space.100', '8px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AttachedFilesContainer = styled.div`
    display: flex;
    flex-flow: row wrap;
    margin-top: ${token('space.200', '16px')};
    text-align: left;
`;

interface DropzoneContainerProps {
    hasError?: boolean;
}
const dropzoneErrorBorder = `solid ${token('color.border.danger', colors.R500)}`;
const dropZoneDashedBorder = `dashed ${token('color.border.input', colors.N20)}`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const DropzoneContainer = styled.div`
    ${mixins.borderRadius}
    border: 2px ${(props: DropzoneContainerProps) => (props.hasError ? dropzoneErrorBorder : dropZoneDashedBorder)};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const AttachedFile = styled.div`
    margin: 0 ${token('space.100', '8px')} ${token('space.100', '8px')} 0;
`;
